import uuid from "uuid/v4";
import { createRuntimeDiagnosticsTransport } from "./runtime-diagnostics-transport";

import { diagnosticBytes, safeId, sanitizeDiagnosticEvent } from "./runtime-diagnostics-schema";
export { sanitizeDiagnosticEvent } from "./runtime-diagnostics-schema";

function classify(details) {
  try {
    const error = details?.error;
    const message = typeof error?.message === "string" ? error.message.slice(0, 512).toLowerCase() : "";
    if (message.includes("pending_session_missing") || message.includes("pending room session was not found"))
      return "pending_session_missing";
    if (error?.status === 401 || error?.status === 403) return "auth_error";
    if (message.includes("timeout") || message.includes("timed out")) return "signaling_timeout";
    if (message.includes("closed")) return "transport_closed";
  } catch (_) {
    /* Ignore hostile objects. */
  }
  return "unknown";
}

export function createRuntimeDiagnostics({
  now = Date.now,
  uuid: newId = uuid,
  send = () => {},
  schedule = setTimeout,
  cancel = clearTimeout
} = {}) {
  let room,
    sessionId,
    attemptId,
    history = [],
    sequence = 0,
    incident,
    timer,
    left = false;
  const transportStates = new Map();
  const prune = () => {
    history = history.filter(x => now() - Date.parse(x.at) <= 120000);
    while (history.length > 200 || diagnosticBytes(history) > 65536)
      history.splice(history[0] === incident?.triggerEvent ? 1 : 0, 1);
  };
  const disarm = () => {
    cancel(timer);
    timer = null;
  };
  const arm = () => {
    if (!incident || timer) return;
    timer = schedule(() => {
      timer = null;
      // eslint-disable-next-line no-use-before-define
      tick();
      arm();
    }, 1000);
    timer?.unref?.();
  };
  const report = (phase, keepalive = false) => {
    if (!incident || incident.reports >= 2) return;
    prune();
    const payload = {
      schemaVersion: 1,
      reportId: newId(),
      diagnosticSessionId: sessionId,
      incidentId: incident.id,
      phase,
      runtimeRoomId: room,
      startedAt: incident.startedAt,
      ...(phase !== "ongoing" ? { endedAt: new Date(now()).toISOString() } : {}),
      events: history.slice()
    };
    while (payload.events.length && diagnosticBytes(payload) > 24576)
      payload.events.splice(payload.events[0] === incident.triggerEvent ? 1 : 0, 1);
    incident.reports++;
    try {
      Promise.resolve(send(payload, keepalive)).catch(() => {});
    } catch (_) {
      /* Reporting never owns RTC control flow. */
    }
  };
  function tick() {
    try {
      prune();
      if (!incident || left) return;
      if (incident.stableAt && now() - incident.stableAt >= 10000) {
        report("recovered");
        incident = null;
        disarm();
      } else if (!incident.stableAt && !incident.reports && now() - Date.parse(incident.startedAt) >= 5000)
        report("ongoing");
    } catch (_) {
      /* Best effort. */
    }
  }
  return {
    session(runtimeRoomId) {
      if (!safeId(runtimeRoomId)) return undefined;
      if (room !== runtimeRoomId || left) {
        disarm();
        room = runtimeRoomId;
        sessionId = newId();
        attemptId = newId();
        history = [];
        transportStates.clear();
        incident = null;
        left = false;
      }
      return sessionId;
    },
    context(runtimeRoomId) {
      if (left || !sessionId || (runtimeRoomId && runtimeRoomId !== room)) return {};
      return {
        diagnosticSessionId: sessionId,
        diagnosticAttemptId: attemptId,
        ...(incident ? { diagnosticIncidentId: incident.id } : {})
      };
    },
    record(event, details, requestContext) {
      try {
        if (requestContext && requestContext.diagnosticSessionId !== sessionId) return;
        if (!room || left || (safeId(details?.roomId) && details.roomId !== room)) return;
        const data = sanitizeDiagnosticEvent(event, details);
        if (data === null) return;
        if (event === "room:leave") {
          left = true;
          incident = null;
          history = [];
          transportStates.clear();
          disarm();
          return;
        }
        const failure =
          ["recovery:error", "recovery:unexpected-close-error"].includes(event) ||
          (event === "dialog-connect:join-error" && details?.suppressRecovery !== true);
        if ((failure || event === "ice-restart:send-error" || event === "ice-restart:recv-error") && !data.code)
          data.code = classify(details);
        const signalingStart = event === "recovery:start" || event === "protoo:disconnected";
        const iceStart = event === "ice-restart:start";
        const starts = signalingStart || iceStart || failure || event === "protoo-request:timeout";
        const newIncident = starts && !incident;
        const role = event === "transport:send-state" ? "send" : event === "transport:recv-state" ? "receive" : null;
        if (role && data.state) transportStates.set(role, data.state);
        if (newIncident)
          incident = {
            id: newId(),
            startedAt: new Date(now()).toISOString(),
            reports: 0,
            iceOnly: iceStart,
            iceRoles: new Set()
          };
        if (starts) {
          incident.stableAt = null;
          if (signalingStart || (iceStart && newIncident)) attemptId = newId();
          if (signalingStart) incident.iceOnly = false;
          if (iceStart && data.transportRole) {
            incident.iceRoles.add(data.transportRole);
            transportStates.set(data.transportRole, "reconnecting");
          }
        }
        history.push({
          sequence: ++sequence,
          at: new Date(now()).toISOString(),
          event,
          attemptId: requestContext?.diagnosticAttemptId ? requestContext.diagnosticAttemptId : attemptId,
          data
        });
        if (event === "protoo-request:timeout" && incident && !incident.triggerEvent)
          incident.triggerEvent = history[history.length - 1];
        prune();
        if (failure) {
          report("failed");
          incident = null;
          disarm();
        } else if (incident) {
          if (
            !incident.iceOnly &&
            (event === "recovery:connected" ||
              event === "dialog-connect:joined" ||
              event === "protoo-request:responsive")
          )
            incident.stableAt = now();
          if (incident.iceOnly && role) {
            if (data.state !== "connected") incident.stableAt = null;
            else if (
              incident.iceRoles.size &&
              [...incident.iceRoles].every(key => transportStates.get(key) === "connected")
            )
              incident.stableAt = incident.stableAt || now();
          }
          arm();
        }
      } catch (_) {
        /* Neither malformed details nor storage/network may affect RTC. */
      }
    },
    isCurrentSession(id) {
      return !left && sessionId === id;
    },
    lifecycle() {
      try {
        if (incident && !incident.reports && !left) report("ongoing", true);
      } catch (_) {
        /* Best effort. */
      }
    },
    tick
  };
}

let apiBase;
const transport = createRuntimeDiagnosticsTransport();
export const runtimeDiagnostics = createRuntimeDiagnostics({
  send: (payload, keepalive) => transport.enqueue(payload, keepalive)
});
let listening = false;
export function setRuntimeDiagnosticsApiBase(value) {
  apiBase = value;
  if (!listening && typeof window !== "undefined") {
    listening = true;
    window.addEventListener("pagehide", () => {
      runtimeDiagnostics.lifecycle();
      transport.flush(true);
    });
    window.addEventListener("online", () => transport.flush());
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        runtimeDiagnostics.lifecycle();
        transport.flush(true);
      }
    });
  }
}
export function configureRuntimeDiagnostics(descriptor, tenantHost) {
  if (runtimeDiagnostics.isCurrentSession(descriptor?.sessionId)) transport.configure(descriptor, apiBase, tenantHost);
}

// Correlation is optional metadata; never allow telemetry to alter an RTC URL.
export function runtimeDiagnosticContext(runtimeRoomId) {
  try {
    return runtimeDiagnostics.context(runtimeRoomId);
  } catch (_) {
    return {};
  }
}
export function withRuntimeDiagnosticQuery(value, context) {
  try {
    const query = new URLSearchParams();
    for (const key of ["diagnosticSessionId", "diagnosticAttemptId", "diagnosticIncidentId"]) {
      const value = context?.[key];
      if (
        typeof value === "string" &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
      )
        query.set(key, value);
    }
    if (!query.size) return value;
    const [base, fragment] = value.split("#", 2);
    return `${base}${base.includes("?") ? "&" : "?"}${query}${fragment === undefined ? "" : `#${fragment}`}`;
  } catch (_) {
    return value;
  }
}
